/*
 * Empyra prepaid discount toggle.
 *
 * Applies / removes the ONLINE5 code via the AJAX cart API. We deliberately do
 * NOT use the /discount/CODE URL: it forces a full page navigation, and in
 * testing it did not reliably preserve cart line items.
 *
 * The cart drawer renders its own correct state from Liquid, so after every
 * toggle we swap in the freshly rendered drawer and re-read state from the DOM.
 * Product pages are cached, so there we resolve state from /cart.js on load.
 *
 * ---------------------------------------------------------------------------
 * DOUBLE-INIT GUARD, added 2026-08-30. Read this before removing it.
 *
 * snippets/empyra-prepaid-toggle.liquid ends with a <script src> tag, and that
 * snippet is rendered TWICE on a product page: once inside the offer card under
 * the buy button, and once inside the cart drawer. Two identical <script src>
 * tags means the browser executes this file twice, which used to register two
 * delegated click listeners on document.
 *
 * The result was that every toggle ran toggle() twice: two cart/update.js
 * requests, two drawer swaps. It was invisible because both passes set the same
 * state, so the UI looked correct.
 *
 * It stopped being invisible the moment analytics was added below: every
 * applied event was reported twice, which would have doubled the numbers in
 * GA4 and Meta and made the offer look twice as popular as it is.
 *
 * ---------------------------------------------------------------------------
 * ANALYTICS, added 2026-08-30.
 *
 * Shopify Admin already tells you how many ORDERS used ONLINE5 (Discounts ->
 * ONLINE5 -> usage). What it cannot tell you is how many people showed intent
 * by applying it, including those who then abandoned. That is the number that
 * says whether the offer is interesting, as opposed to whether it converted.
 *
 * So we fire a custom event on every successful toggle, to whichever of the
 * three sinks happen to exist on the page:
 *
 *   Meta Pixel  fbq('trackCustom', 'PrepaidDiscountApplied' | '...Removed')
 *               -> Events Manager, and usable as a Custom Conversion / audience
 *   GA4         gtag('event', 'prepaid_discount_applied' | '...removed')
 *               -> Reports > Engagement > Events
 *   dataLayer   { event: 'empyra_prepaid_toggle' }  for GTM, if ever added
 *
 * Every call is feature-detected and wrapped in try/catch. Ad blockers remove
 * fbq and gtag routinely in this market, and an analytics failure must never
 * stop the discount from being applied. Tracking is strictly best-effort.
 *
 * We fire on SUCCESS, not on click. A click whose cart request failed did not
 * get the customer a discount, and counting it would overstate interest.
 */
(function () {
  'use strict';

  if (window.__empyraPrepaidReady) return;
  window.__empyraPrepaidReady = true;

  var CODE = 'ONLINE5';
  var SELECTOR = '[data-empyra-prepaid]';

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function nodes() {
    return Array.prototype.slice.call(document.querySelectorAll(SELECTOR));
  }

  function track(applied) {
    var payload = {
      code: CODE,
      discount_percent: 5,
      applied: applied,
      surface: document.querySelector('#CartDrawer.active') ? 'cart' : 'product'
    };

    try {
      if (typeof window.fbq === 'function') {
        window.fbq(
          'trackCustom',
          applied ? 'PrepaidDiscountApplied' : 'PrepaidDiscountRemoved',
          payload
        );
      }
    } catch (e) { /* analytics must never break the toggle */ }

    try {
      if (typeof window.gtag === 'function') {
        window.gtag(
          'event',
          applied ? 'prepaid_discount_applied' : 'prepaid_discount_removed',
          payload
        );
      }
    } catch (e) { /* as above */ }

    try {
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push({ event: 'empyra_prepaid_toggle', prepaid_applied: applied });
      }
    } catch (e) { /* as above */ }
  }

  function paint(on) {
    nodes().forEach(function (el) {
      el.setAttribute('data-state', on ? 'on' : 'off');
      el.removeAttribute('data-busy');
      var btns = el.querySelectorAll('button');
      Array.prototype.forEach.call(btns, function (b) { b.disabled = false; });
    });
  }

  function busy(flag) {
    nodes().forEach(function (el) {
      if (flag) { el.setAttribute('data-busy', 'true'); } else { el.removeAttribute('data-busy'); }
      var btns = el.querySelectorAll('button');
      Array.prototype.forEach.call(btns, function (b) { b.disabled = !!flag; });
    });
  }

  function syncFromCart() {
    if (!nodes().length) return;
    fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var codes = cart.discount_codes || [];
        var on = false;
        for (var i = 0; i < codes.length; i++) {
          if (codes[i].code === CODE && codes[i].applicable) { on = true; break; }
        }
        paint(on);
      })
      .catch(function () { /* leave the server-rendered default in place */ });
  }

  function swapDrawer(html) {
    if (!html) return false;
    var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('#CartDrawer');
    var live = document.querySelector('#CartDrawer');
    if (!fresh || !live) return false;
    live.innerHTML = fresh.innerHTML;
    return true;
  }

  function toggle(el) {
    var turningOn = el.getAttribute('data-state') !== 'on';
    busy(true);

    fetch(root() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        discount: turningOn ? CODE : '',
        sections: 'cart-drawer'
      })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data && data.sections) swapDrawer(data.sections['cart-drawer']);
        paint(turningOn);
        track(turningOn);
        document.dispatchEvent(new CustomEvent('empyra:prepaid:changed', { detail: { applied: turningOn } }));
      })
      .catch(function () {
        busy(false);
        syncFromCart();
      });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest(SELECTOR + ' button') : null;
    if (!btn) return;
    e.preventDefault();
    toggle(btn.closest(SELECTOR));
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncFromCart);
  } else {
    syncFromCart();
  }
})();
